import { useEffect, useState } from "react";
import { Smartphone, Tablet, Monitor, LogOut, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };
const ago = (iso) => {
    const m = Math.max(0, Math.floor((Date.now() - new Date(iso)) / 60000));
    if (m < 1) return "právě teď";
    if (m < 60) return `před ${m} min`;
    if (m < 1440) return `před ${Math.floor(m / 60)} h`;
    return new Date(iso).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" });
};

/** Seznam přihlášených zařízení. Bez `memberId` = moje relace (/auth/sessions), s `memberId` = zařízení člena týmu (jen vlastník). */
export function DeviceList({ memberId, onLoggedOutSelf }) {
    const [items, setItems] = useState(null);
    const base = memberId ? `/team/members/${memberId}/sessions` : "/auth/sessions";
    const load = () => api.get(base).then((r) => setItems(r.data)).catch(() => setItems([]));
    useEffect(() => { load(); }, [memberId]); // eslint-disable-line

    const revoke = async (s) => {
        if (s.current && !window.confirm("Odhlásit toto zařízení? Budete odhlášeni.")) return;
        try {
            const r = await api.delete(`${base}/${s.id}`);
            if (r.data.current) { onLoggedOutSelf?.(); return; }
            toast.success("Zařízení odhlášeno"); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Odhlášení selhalo"); }
    };
    const revokeOthers = async () => {
        try {
            const r = memberId ? await api.delete(base) : await api.post("/auth/sessions/logout-others");
            toast.success(`Odhlášeno zařízení: ${r.data.revoked}`); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Odhlášení selhalo"); }
    };

    if (!items) return <div className="text-xs text-slate-400 px-6 py-4">Načítání…</div>;
    return (
        <div data-testid={memberId ? `devices-${memberId}` : "my-devices"}>
            {items.length === 0 && <div className="text-sm text-slate-400 px-6 py-4">Žádné přihlášené zařízení.</div>}
            <ul className="divide-y divide-slate-100">
                {items.map((s) => {
                    const I = ICON[s.device?.type] || Monitor;
                    return (
                        <li key={s.id} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`device-${s.id}`}>
                            <I className="w-5 h-5 text-slate-500 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                                    {s.device?.label}{s.device?.pwa && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-slate-300 text-slate-600">aplikace</span>}
                                    {s.current && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-50 border border-emerald-400 text-emerald-800" data-testid={`device-current-${s.id}`}>toto zařízení</span>}
                                </div>
                                <div className="text-xs text-slate-500 tabular-data">Aktivita {ago(s.last_seen_at)} · IP {s.ip || "—"} · přihlášeno {new Date(s.created_at).toLocaleDateString("cs-CZ")}</div>
                            </div>
                            <button onClick={() => revoke(s)} className="min-h-[40px] px-3 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:border-red-500 hover:text-red-700 rounded-sm inline-flex items-center gap-1" data-testid={`device-logout-${s.id}`}><LogOut className="w-3.5 h-3.5" /> Odhlásit toto zařízení</button>
                        </li>
                    );
                })}
            </ul>
            {items.length > (memberId ? 0 : 1) && (
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-end">
                    <button onClick={revokeOthers} className="min-h-[40px] px-4 text-[10px] uppercase tracking-wider font-semibold bg-slate-900 text-white hover:bg-red-700 rounded-sm inline-flex items-center gap-1" data-testid={memberId ? `devices-logout-all-${memberId}` : "devices-logout-others"}><ShieldOff className="w-3.5 h-3.5" /> {memberId ? "Odhlásit všechna zařízení" : "Odhlásit všechna ostatní"}</button>
                </div>
            )}
        </div>
    );
}
